import { yupResolver } from '@hookform/resolvers/yup'
import { Button, Flex, Form } from 'antd'
import { useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { menuUrls } from 'src/app/shared/constants/menu.constants'
import { SYSTEM_MESSAGE_IDS } from 'src/app/shared/constants/systemMessages.constants'
import { IClientSetup } from 'src/app/shared/models/clientSetup.model'
import { addClientSetup, checkClientDuplicates } from 'src/app/shared/services/ClientSetupService'
import { getLookupValues } from 'src/app/store/masterSetup'

import ACHException from './ACHException'
import CheckExceptions from './CheckExceptions'
import ClientInfo from './ClientInfo'
import DelayedClientActivation from './DelayedClientActivation'
import './index.scss'
import MakeClientActiveModal from './MakeClientActiveModal'
import NoExceptionModal from './NoExceptionModal'
import { formatDataBeforeSubmit, generateValidationSchema, RESET_FORM_FIELDS } from './utils'
import { fetchGlobalExceptionDefaults } from '../edit-client-setup/utils'

const ClientSetupForm = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const systemDefaultConfig = useSelector((state: any) => state?.masterSetup?.deferredConfig?.systemDefaults)
  const retentionSettings = useSelector((state: any) => state?.masterSetup?.deferredConfig?.retentionSettings)
  const lookupValues = useSelector(getLookupValues)
  const { staleDatedCheck } = retentionSettings || {}
  const [isLoading, setIsLoading] = useState(false)

  const [isNoExceptionModalOpen, setIsNoExceptionModalOpen] = useState(false)

  const [isMakeClientActiveModalOpen, setIsMakeClientActiveModalOpen] = useState(false)
  const [duplicatedClient, setDuplicatedClient] = useState({
    archived: null,
    clientName: null,
    clientNumber: 0,
    id: null,
  })

  const { AchAuthorizationRulesNotificationType } = lookupValues || {}

  const {
    defaultTimeZone,
    // Check Exceptions
    defaultCheckExceptionsProcessingType,
    defaultCheckDecision,
    defaultCheckReason,
    // ACH Exceptions
    defaultAchExceptionsProcessingType,
    defaultAchDecision,
    defaultAchReason,
  } = systemDefaultConfig || {}

  const defaultValues: IClientSetup = {
    staleDatedCheck,
    id: null,
    // Client Info
    clientName: '',
    clientNumber: null,
    referenceCode: '',
    clientNotes: '',
    timeZoneId: defaultTimeZone?.id || '',
    // Sourced from the global config after mount (see applyGlobalExceptionDefaults) — start empty.
    exceptionReminderEmailTimeInMinutes: '' as any,
    // Check Exceptions
    defaultCheckExceptionCreationType: defaultCheckExceptionsProcessingType || '',
    defaultCheckExceptionCutoffTime: '',
    defaultCheckDecision: defaultCheckDecision || '',
    defaultCheckReason: defaultCheckReason || '',
    issuedCheckFileFormats: [],
    displayIssuedPayee: true,
    payeeMatch: false,
    payeeMatchParameterId: null,
    displayNotes: false,
    autoLoadIssuedCheckFiles: false,
    sftpFileLocation: '',
    // ACH Exceptions
    defaultAchExceptionCreationType: defaultAchExceptionsProcessingType || '',
    defaultAchExceptionCutoffTime: '',
    defaultAchDecision: defaultAchDecision || '',
    defaultAchReason: defaultAchReason || '',
    achAuthorizationRulesNotificationType:
      AchAuthorizationRulesNotificationType?.find((item: any) => item?.default)?.value || '',
    // Archive when editing
    archived: false,
    activationDate: null
  }

  const {
    control,
    formState: { errors, touchedFields, isValid },
    resetField,
    handleSubmit,
    getValues,
    setValue,
    clearErrors,
    setError,
    setFocus,
  } = useForm<any>({ mode: 'all', defaultValues, resolver: yupResolver(generateValidationSchema(t)) })

  const [globalExceptionDefaults, setGlobalExceptionDefaults] = useState<any>(null)

  useEffect(() => {
    setFocus('clientName')
  }, [setFocus])

  // The 3 exception fields start empty and are populated from the global System-Wide Rules config.
  useEffect(() => {
    ;(async () => {
      const values = await fetchGlobalExceptionDefaults()
      if (!values) return
      setGlobalExceptionDefaults(values)
      setValue('exceptionReminderEmailTimeInMinutes', values.exceptionReminderEmailTimeInMinutes)
      setValue('defaultCheckExceptionCutoffTime', values.defaultCheckExceptionCutoffTime)
      setValue('defaultAchExceptionCutoffTime', values.defaultAchExceptionCutoffTime)
      setValue('timeZoneId', values.timeZoneId)
    })()
  }, [setValue])

  useEffect(() => {
    if (!isCreateAchExceptions && !isCreateExceptions) {
      setError('defaultCheckExceptionCreationType', {
        type: 'manual',
        message: t('At least one exception type (ACH or Check) must be enabled to proceed.'),
      })
      setError('defaultAchExceptionCreationType', {
        type: 'manual',
        message: t('At least one exception type (ACH or Check) must be enabled to proceed.'),
      })
    } else {
      clearErrors(['defaultCheckExceptionCreationType', 'defaultAchExceptionCreationType'])
    }
  })

  const isDisplayIssuedPayee = useWatch({
    control,
    name: 'displayIssuedPayee',
  })

  const isPayeeMatchChecked = useWatch({
    control,
    name: 'payeeMatch',
  })

  const defaultCheckExceptionCreationType = useWatch({
    control,
    name: 'defaultCheckExceptionCreationType',
  })

  const defaultAchExceptionCreationType = useWatch({
    control,
    name: 'defaultAchExceptionCreationType',
  })

  const autoLoadIssuedCheckFiles = useWatch({
    control,
    name: 'autoLoadIssuedCheckFiles',
  })

  const isAutoloadIssuedChecked = useWatch({
    control,
    name: 'autoLoadIssuedCheckFiles',
  })

  const isCreateExceptions = defaultCheckExceptionCreationType !== 'NOCREATE'
  const isCreateAchExceptions = defaultAchExceptionCreationType !== 'NOCREATE'

  useEffect(() => {
    if (!autoLoadIssuedCheckFiles) clearErrors('sftpFileLocation')

    if (!isPayeeMatchChecked) clearErrors('payeeMatchParameterId')
  }, [clearErrors, autoLoadIssuedCheckFiles, isPayeeMatchChecked])

  useEffect(() => {
    if (!isCreateExceptions) {
      clearErrors('issuedCheckFileFormats')

      for (let i = 0; i <= RESET_FORM_FIELDS.CHECK_EXCEPTIONS?.length; i++) {
        resetField(RESET_FORM_FIELDS.CHECK_EXCEPTIONS?.[i])
      }
    } else if (globalExceptionDefaults) {
      setValue('defaultCheckExceptionCutoffTime', globalExceptionDefaults.defaultCheckExceptionCutoffTime)
    }
  }, [isCreateExceptions, resetField, clearErrors, setValue, globalExceptionDefaults])

  useEffect(() => {
    if (!isCreateAchExceptions) {
      for (let i = 0; i <= RESET_FORM_FIELDS.ACH_CHECK_CHECK_EXCEPTIONS?.length; i++) {
        resetField(RESET_FORM_FIELDS.ACH_CHECK_CHECK_EXCEPTIONS?.[i])
      }
    } else if (globalExceptionDefaults) {
      setValue('defaultAchExceptionCutoffTime', globalExceptionDefaults.defaultAchExceptionCutoffTime)
    }
  }, [isCreateAchExceptions, resetField, setValue, globalExceptionDefaults])

  const handleCancel = () => {
    navigate(menuUrls.clientSetup)
  }

  useEffect(() => {
    if (!isDisplayIssuedPayee) {
      resetField('payeeMatch')
      resetField('payeeMatchParameterId')
    }
    if (!isPayeeMatchChecked) {
      resetField('payeeMatchParameterId')
    }

    if (!isAutoloadIssuedChecked) {
      resetField('sftpFileLocation')
    }
  }, [isDisplayIssuedPayee, isPayeeMatchChecked, isAutoloadIssuedChecked, resetField])

  const checkDuplicates = async (clientName: string, clientNumber: string | null) => {
    if (clientName && clientNumber) {
      const response = await checkClientDuplicates(clientName, clientNumber)
      return response?.data
    }
  }

  const addClient = async (data: IClientSetup) => {
    setIsLoading(true)
    const res = await addClientSetup(formatDataBeforeSubmit(data))
    const { statusCode } = res
    setIsLoading(false)
    // Then show toast message if success
    if (statusCode === 'Success') {
      navigate(menuUrls.clientSetup, {
        state: {
          clientSetup: {
            type: 'success',
            message: data?.id
              ? t('SystemMessages:' + SYSTEM_MESSAGE_IDS.CLIENT_NAME_ACTIVE, {
                Client_Name: res?.data?.clientName,
              })
              : t('SystemMessages:' + SYSTEM_MESSAGE_IDS.CLIENT_NAME_CREATED_SUCCESSFULLY, {
                Client_Name: res?.data?.clientName,
              }),
          },
        },
      })
    }
    return
  }

  const onSubmit = async (data: IClientSetup) => {
    // User select "Don't Create Exception" in Check Exception and ACH Exception then show No Exception Modal
    if (!isCreateExceptions && !isCreateAchExceptions) {
      setIsNoExceptionModalOpen(true)
      return
    }

    // Check duplicate
    const { clientName, clientNumber } = data
    setIsLoading(true)
    const response = await checkDuplicates(clientName, clientNumber)
    setIsLoading(false)
    setDuplicatedClient(response)

    // Not duplicate
    if (!response?.isDuplicate) {
      addClient(data)
    }

    // Active ( archive === false)
    if (!response?.archived) {
      if (getValues('clientNumber') === response?.clientNumber)
        setError('clientNumber', { message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.CLIENT_IS_ALREADY_ACTIVE) })
      if (getValues('clientName')?.toLowerCase() === response?.clientName?.toLowerCase())
        setError('clientName', { message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.CLIENT_IS_ALREADY_ACTIVE) })
      return
    }
    // Inactive
    // OLD LOGIC: setIsMakeClientActiveModalOpen(true)
    // NOTE: when duplicated client is inactive, system redirects to that duplicated client
    // NOTE: adjustment GBBM-83
    if (response?.id) {
      navigate(`${menuUrls.editClientSetup}/${response.id}`)
    }
  }

  // Click Yes in No Exception Modal
  const handleConfirmNoExceptionModal = () => {
    setIsNoExceptionModalOpen(false)
  }

  const handleConfirmMakeClientActiveModal = async () => {
    setValue('id', duplicatedClient.id)
    setValue('sourceId', duplicatedClient.id)
    const formData = getValues()
    await addClient(formData)
    onCloseMakeClientActiveModalOpen()
  }

  const onCloseHasExceptionsToResolveModal = () => {
    navigate(menuUrls.clientSetup)
  }

  const onCloseMakeClientActiveModalOpen = () => {
    setIsMakeClientActiveModalOpen(false)
  }

  const isSaveButtonDisabled =
    !!Object.keys(errors)?.length ||
    !Object.keys(touchedFields)?.length ||
    !isValid ||
    !getValues('clientName')?.trim() ||
    !getValues('clientNumber') ||
    (isPayeeMatchChecked && !getValues('payeeMatchParameterId')) ||
    (isAutoloadIssuedChecked && !getValues('sftpFileLocation'))

  return (
    <Form name='client-setup-form' className='add-client-form-container' layout='vertical'>
      <ClientInfo
        control={control}
        errors={errors}
        getValues={getValues}
        displayDuplicateText={!isMakeClientActiveModalOpen}
      />

      <CheckExceptions
        control={control}
        isDisplayIssuedPayee={isDisplayIssuedPayee}
        isCreateExceptions={isCreateExceptions}
        isPayeeMatchChecked={isPayeeMatchChecked}
        autoLoadIssuedCheckFiles={autoLoadIssuedCheckFiles}
        errors={errors}
        setValue={setValue}
      />

      <ACHException
        control={control}
        isCreateAchExceptions={isCreateAchExceptions}
        errors={errors}
      />

      <DelayedClientActivation control={control} />

      <Flex className='button-group' justify='flex-end' gap={24}>
        <Button className='cancel-button' onClick={handleCancel}>
          {t('OverallFunctionality:cancel_btn')}
        </Button>

        <Button
          className='save-button'
          type='primary'
          disabled={isSaveButtonDisabled}
          htmlType='button'
          loading={isLoading}
          onClick={handleSubmit(onSubmit)}
        >
          {t('OverallFunctionality:save_changes_btn')}
        </Button>
      </Flex>

      <NoExceptionModal
        onConfirm={handleConfirmNoExceptionModal}
        onClose={onCloseHasExceptionsToResolveModal}
        isOpen={isNoExceptionModalOpen}
      />

      <MakeClientActiveModal
        clientName={getValues('clientName')}
        onConfirm={handleConfirmMakeClientActiveModal}
        onClose={onCloseMakeClientActiveModalOpen}
        isOpen={isMakeClientActiveModalOpen}
        isLoading={isLoading}
      />
    </Form>
  )
}

export default ClientSetupForm
